import { useEffect, useState, useCallback } from 'react';
import { useRoutingStore } from '@/stores/routingStore';
import { useUIStore } from '@/stores/uiStore';
import { searchService } from '@/services/searchService';
import { copyToClipboard } from '@/utils/clipboard';
import type { Coordinates } from '@/types';
import { useSharedLocationStore } from './sharedLocationStore';
import { buildPinLink } from './shareUrl';

/**
 * Small floating card shown when a shared-location pin is active (opened a
 * `?ll=` link, or dropped one via the Share flow). Shows the coordinates, a
 * reverse-geocoded address when available, and a "Directions to here" action
 * that seeds the directions panel — the robust "come to this spot" hand-off.
 */
export function SharedLocationCard() {
  const pin = useSharedLocationStore((s) => s.pin);
  const clearPin = useSharedLocationStore((s) => s.clearPin);
  const setWaypoint = useRoutingStore((s) => s.setWaypoint);
  const setActiveWaypointId = useRoutingStore((s) => s.setActiveWaypointId);
  const openPanel = useUIStore((s) => s.openPanel);
  const addNotification = useUIStore((s) => s.addNotification);

  const [address, setAddress] = useState<string | null>(null);

  const coordLabel = pin ? `${pin.lat.toFixed(6)}, ${pin.lng.toFixed(6)}` : '';

  // Enrich the card with a real address. Guard against a stale response
  // overwriting the label after the pin changed / cleared.
  useEffect(() => {
    setAddress(null);
    if (!pin) return;
    let active = true;
    searchService
      .reverseGeocode(pin)
      .then((r) => {
        if (active && r?.address?.formatted) setAddress(r.address.formatted);
      })
      .catch(() => {/* keep coordinate label */});
    return () => {
      active = false;
    };
  }, [pin]);

  const handleDirections = useCallback((coords: Coordinates) => {
    setActiveWaypointId(null);
    setWaypoint('destination', coords, address || coordLabel);
    openPanel('directionsPanel');
  }, [setActiveWaypointId, setWaypoint, address, coordLabel, openPanel]);

  const handleCopy = useCallback(async (coords: Coordinates) => {
    const ok = await copyToClipboard(buildPinLink(coords));
    addNotification({
      type: ok ? 'success' : 'error',
      message: ok ? 'Link copied' : 'Failed to copy link',
      duration: 3000,
    });
  }, [addNotification]);

  if (!pin) return null;

  return (
    <div className="absolute bottom-8 left-1/2 z-panel w-80 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-xl bg-white p-4 shadow-card animate-fade-in">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <span aria-hidden className="mt-0.5 text-lg leading-none">📍</span>
          <div>
            <h2 className="text-sm font-medium text-gray-900">Shared location</h2>
            <p className="mt-0.5 text-sm text-gray-600">{address || coordLabel}</p>
            {address && (
              <p className="mt-0.5 text-xs text-gray-400">{coordLabel}</p>
            )}
          </div>
        </div>
        <button
          onClick={clearPin}
          aria-label="Dismiss shared location"
          className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="mt-3 flex gap-2">
        <button
          onClick={() => handleDirections(pin)}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary-600 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
        >
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M22.43 10.59l-9.01-9.01c-.75-.75-2.07-.76-2.83 0l-9 9c-.78.78-.78 2.04 0 2.82l9 9c.39.39.9.58 1.41.58.51 0 1.02-.19 1.41-.58l9.01-9.01c.78-.78.78-2.04.01-2.8zM12.5 18v-4h-2v-2h2V8l4 4-4 4z" />
          </svg>
          Directions to here
        </button>
        <button
          onClick={() => handleCopy(pin)}
          title="Copy shareable link"
          className="rounded-lg border border-gray-300 p-2 text-gray-600 transition-colors hover:bg-gray-50"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
        </button>
      </div>
    </div>
  );
}
